import React, { useState, useEffect, useRef } from 'react';
import { Target, Sparkles, Rocket, GraduationCap, TrendingUp, Users } from 'lucide-react';

const LAUNCH_TIME = new Date('2026-04-07T06:28:00Z'); // 11:58 AM IST = 06:28 UTC

const CountdownPage = ({ onLaunch }) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [finalCountdown, setFinalCountdown] = useState(null);
  const [launched, setLaunched] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const diff = LAUNCH_TIME - now;

      if (diff <= 0 && !finalCountdown && !launched) {
        setFinalCountdown(5);
        return;
      }

      if (diff > 0) {
        const d = Math.floor(diff / (1000 * 60 * 60 * 24));
        const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const m = Math.floor((diff / (1000 * 60)) % 60);
        const s = Math.floor((diff / 1000) % 60);
        setTimeLeft({ days: d, hours: h, minutes: m, seconds: s });
      }
    };

    calc();
    tickRef.current = setInterval(calc, 1000);
    return () => clearInterval(tickRef.current);
  }, [finalCountdown, launched]);

  useEffect(() => {
    if (finalCountdown === null) return;
    if (finalCountdown === 0) {
      setTimeout(() => {
        setLaunched(true);
        setTimeout(() => onLaunch(), 1500);
      }, 800);
      return;
    }
    const t = setTimeout(() => setFinalCountdown(finalCountdown - 1), 1000);
    return () => clearTimeout(t);
  }, [finalCountdown, onLaunch]);

  if (launched) {
    return (
      <div className="fixed inset-0 bg-earth-700 flex items-center justify-center z-[9999]" data-testid="launch-reveal">
        <div className="text-center animate-scale-up">
          <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Target className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight">O4S.ai</h1>
          <p className="text-xl text-earth-300 mt-2 font-light">is LIVE</p>
          <Rocket className="w-8 h-8 text-primary mx-auto mt-4 animate-bounce" />
        </div>
      </div>
    );
  }

  if (finalCountdown !== null) {
    return (
      <div className="fixed inset-0 bg-earth-700 flex items-center justify-center z-[9999]" data-testid="final-countdown">
        <div className="text-center">
          <p className="text-earth-400 text-lg mb-4 tracking-widest uppercase">Launching in</p>
          <div className="relative">
            <span
              key={finalCountdown}
              className="text-[12rem] md:text-[16rem] font-black text-primary leading-none inline-block"
              style={{ animation: 'countPulse 1s ease-out' }}
            >
              {finalCountdown}
            </span>
          </div>
          <div className="flex justify-center gap-1 mt-6">
            {[5, 4, 3, 2, 1].map(n => (
              <div
                key={n}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  n >= finalCountdown ? 'bg-primary scale-110' : 'bg-earth-600'
                }`}
              />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes countPulse {
            0% { transform: scale(0.3); opacity: 0; }
            50% { transform: scale(1.15); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  const features = [
    { icon: GraduationCap, label: 'AI Career Guidance', desc: 'Personalized paths for Indian students' },
    { icon: TrendingUp, label: 'Skill2Income Engine', desc: 'Know your earning potential' },
    { icon: Users, label: 'Expert-Curated FAQs', desc: 'AP & Telangana focused insights' },
    { icon: Sparkles, label: 'Powered by GPT-5.2', desc: 'Advanced AI recommendations' },
  ];

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="min-h-screen bg-earth-700 text-white flex flex-col" data-testid="countdown-page">
      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(217, 90, 65, 0.3); }
          50% { box-shadow: 0 0 40px rgba(217, 90, 65, 0.6); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .shimmer-text {
          background: linear-gradient(90deg, #D95A41, #FFB088, #D95A41);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 3s linear infinite;
        }
        .glow-box { animation: glow 2s ease-in-out infinite; }
        .tick-flip {
          animation: tickIn 0.4s ease-out;
        }
        @keyframes tickIn {
          0% { transform: rotateX(90deg); opacity: 0; }
          100% { transform: rotateX(0); opacity: 1; }
        }
      `}</style>

      {/* Nav */}
      <nav className="px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight">O4S.ai</span>
        </div>
        <span className="text-xs text-earth-400 tracking-wider uppercase hidden sm:block">Opportunities for Students</span>
      </nav>

      {/* Hero */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="animate-float mb-6">
          <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center glow-box">
            <Rocket className="w-8 h-8 text-primary" />
          </div>
        </div>

        <p className="text-earth-400 text-sm tracking-[0.3em] uppercase mb-3">Launching On</p>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-center mb-2">
          <span className="shimmer-text">April 7, 2026</span>
        </h1>
        <p className="text-earth-400 text-base mb-10">11:58 AM IST</p>

        {/* Countdown Boxes */}
        <div className="grid grid-cols-4 gap-3 sm:gap-5 mb-12 max-w-lg w-full">
          {[
            { val: timeLeft.days, label: 'Days' },
            { val: timeLeft.hours, label: 'Hours' },
            { val: timeLeft.minutes, label: 'Minutes' },
            { val: timeLeft.seconds, label: 'Seconds' },
          ].map(({ val, label }) => (
            <div key={label} className="text-center">
              <div className="bg-earth-600/50 backdrop-blur-sm border border-earth-500/30 rounded-xl p-3 sm:p-5 mb-2">
                <span key={val} className="text-3xl sm:text-5xl md:text-6xl font-black text-white tick-flip inline-block">
                  {pad(val || 0)}
                </span>
              </div>
              <span className="text-xs sm:text-sm text-earth-400 uppercase tracking-wider">{label}</span>
            </div>
          ))}
        </div>

        {/* Tagline */}
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-center text-white mb-2">
          AI-Powered Career Guidance for Indian Students
        </h2>
        <p className="text-earth-400 text-sm text-center max-w-md mb-10">
          Discover the perfect career path tailored to your education, skills & dreams. Built for AP & Telangana students.
        </p>

        {/* Feature Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl w-full">
          {features.map(({ icon: Icon, label, desc }) => (
            <div key={label} className="bg-earth-600/30 border border-earth-500/20 rounded-xl p-4 text-center hover:bg-earth-600/50 transition-colors">
              <Icon className="w-5 h-5 text-primary mx-auto mb-2" />
              <p className="text-white text-xs font-semibold mb-1">{label}</p>
              <p className="text-earth-400 text-[10px] leading-tight">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 py-5 text-center border-t border-earth-600/30">
        <p className="text-earth-500 text-xs">
          &copy; 2026 O4S.ai by KKM.Digital — Hyderabad, India
        </p>
      </footer>
    </div>
  );
};

export default CountdownPage;
